import { useNavigate } from "react-router-dom"
import { motion } from "framer-motion"
import { useEffect, useState } from "react"
import Layout from "@/components/Layout/Layout"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Trophy, ExternalLink, Gamepad2, ArrowRight, Flame, Calendar } from "lucide-react"
import { useAllGames } from "@/graphql/hooks/useGames"
import type { Game } from "@/graphql/types"

const gameCardSkeleton = [1, 2, 3, 4]

const AnimatedMetricValue = ({
  value,
  loading,
  delay = 0,
}: {
  value: number
  loading: boolean
  delay?: number
}) => {
  const [displayValue, setDisplayValue] = useState(0)

  useEffect(() => {
    if (loading) {
      setDisplayValue(0)
      return
    }

    let frameId = 0
    let timerId: ReturnType<typeof setTimeout> | undefined
    const duration = 900

    const startAnimation = () => {
      const startTime = performance.now()

      const animate = (currentTime: number) => {
        const elapsed = currentTime - startTime
        const progress = Math.min(elapsed / duration, 1)
        const easedProgress = 1 - Math.pow(1 - progress, 3)
        const nextValue = Math.round(value * easedProgress)

        setDisplayValue(nextValue)

        if (progress < 1) {
          frameId = requestAnimationFrame(animate)
        }
      }

      frameId = requestAnimationFrame(animate)
    }

    if (delay > 0) {
      timerId = setTimeout(startAnimation, delay)
    } else {
      startAnimation()
    }

    return () => {
      if (frameId) cancelAnimationFrame(frameId)
      if (timerId) clearTimeout(timerId)
    }
  }, [delay, loading, value])

  if (loading) return <>--</>

  return <>{displayValue.toLocaleString()}</>
}

const Ranking = () => {
  const navigate = useNavigate()

  // Load games data
  const { games, loading: gamesLoading, error: gamesError } = useAllGames()

  const totalTournaments = games.reduce((acc, game) => acc + Number(game.totalTournaments || 0), 0)
  const activeGames = games.filter((game) => game.isActive).length
  const gamesWithSeason = games.filter((game) => Boolean(game.urlActiveSeason)).length

  const metricItems = [
    { label: "Juegos", value: activeGames, delay: 0 },
    { label: "Torneos", value: totalTournaments, delay: 120 },
    { label: "Temporadas", value: gamesWithSeason, delay: 240 },
  ]

  const handleGameClick = (game: Game) => {
    if (game.urlActiveSeason) {
      navigate(game.urlActiveSeason)
    }
  }

  return (
    <Layout>
      <div className="min-h-screen overflow-x-hidden">
        <section className="relative overflow-hidden px-4 pt-8 pb-6 sm:px-6 sm:pt-10 sm:pb-8 md:pt-14 md:pb-10 lg:px-8">
          <div className="container mx-auto p-0">
            <div className="grid gap-8 lg:grid-cols-[1.1fr_0.9fr] lg:items-center">
              <div className="space-y-6">
                <Badge className="inline-flex gap-2 border-primary/25 bg-primary/10 px-3 py-1 text-primary">
                  <Flame className="h-3.5 w-3.5" />
                  Clasificaciones y temporadas activas
                </Badge>

                <h1 className="text-4xl font-black leading-tight tracking-tight md:text-6xl">
                  Rankings y
                  <span className="block bg-gradient-to-r from-primary via-cyan-400 to-primary bg-[length:200%_200%] bg-clip-text text-transparent animate-gradient">
                    Juegos
                  </span>
                </h1>

                <p className="max-w-2xl text-lg leading-relaxed text-muted-foreground md:text-xl">
                  Explora juegos, entra a su temporada activa y descubre quién lidera cada
                  temporada.
                </p>

                <div className="flex flex-col gap-3 sm:flex-row">
                  <Button
                    size="lg"
                    className="h-12 bg-gradient-primary px-7 text-base shadow-[0_8px_30px_hsl(var(--primary)/0.35)]"
                    onClick={() => navigate("/tournaments")}
                  >
                    <Calendar className="mr-2 h-5 w-5" />
                    Ver Eventos
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>

                  <Button
                    variant="outline"
                    size="lg"
                    className="h-12 px-7 text-base"
                    onClick={() => navigate("/")}
                  >
                    <Gamepad2 className="mr-2 h-5 w-5" />
                    Ir al Inicio
                  </Button>
                </div>
              </div>

              <Card className="border-primary/15 bg-card/65 backdrop-blur-xl shadow-[0_10px_45px_rgba(2,6,23,0.08)]">
                <CardHeader className="px-4 pb-3 sm:px-6">
                  <CardTitle className="text-xl">Pulso del Ranking</CardTitle>
                  <CardDescription>Vista rápida del ecosistema competitivo</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4 px-4 sm:px-6">
                  <div className="grid grid-cols-3 gap-2 sm:gap-3">
                    {metricItems.map((metric, index) => (
                      <motion.div
                        key={metric.label}
                        className="rounded-xl border border-border/70 bg-background/70 p-2 text-center sm:p-3"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.35, delay: index * 0.08 }}
                      >
                        <p className="text-lg font-bold tabular-nums sm:text-2xl">
                          <AnimatedMetricValue
                            value={metric.value}
                            loading={gamesLoading}
                            delay={metric.delay}
                          />
                        </p>
                        <p className="mt-0.5 truncate text-[11px] text-muted-foreground sm:text-xs">
                          {metric.label}
                        </p>
                      </motion.div>
                    ))}
                  </div>

                  <div className="rounded-2xl border border-border/70 bg-gradient-to-r from-primary/10 via-transparent to-cyan-500/10 p-4">
                    <p className="text-sm text-muted-foreground">
                      ¿Buscas clasificaciones activas?
                    </p>
                    <p className="mt-1 text-base font-semibold">
                      Entra a la temporada de cada juego para revisar sus tablas.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        <section className="px-4 py-6 sm:px-6 sm:py-8 md:py-12 lg:px-8">
          <div className="container mx-auto max-w-7xl p-0">
            <div className="mb-8 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
              <div>
                <h2 className="text-3xl font-bold md:text-4xl">Juegos con Ranking</h2>
                <p className="mt-2 max-w-2xl text-muted-foreground">
                  Selecciona un juego para abrir su temporada activa y explorar posiciones.
                </p>
              </div>
              <Button variant="outline" onClick={() => navigate("/tournaments")}>
                Ver todos los eventos
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>

            {/* Games Section */}
            <section className="space-y-6">
              {gamesLoading ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {gameCardSkeleton.map((i) => (
                    <div
                      key={i}
                      className="animate-pulse rounded-2xl border border-border/50 overflow-hidden"
                    >
                      <div className="h-52 bg-muted" />
                      <div className="p-3 space-y-2">
                        <div className="h-3 bg-muted rounded w-3/4" />
                        <div className="h-7 bg-muted rounded" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : gamesError ? (
                <div className="text-center py-12">
                  <p className="text-destructive">Error al cargar los juegos</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {games.map((game) => (
                    <div
                      key={game.id}
                      className={`group relative overflow-hidden rounded-2xl border border-border/50 bg-card/70 backdrop-blur-sm transition-all duration-300 ${
                        game.urlActiveSeason
                          ? "cursor-pointer hover:border-primary/40 hover:shadow-xl hover:-translate-y-0.5"
                          : "opacity-75"
                      }`}
                      onClick={() => game.urlActiveSeason && handleGameClick(game)}
                    >
                      {/* Image */}
                      <div className="relative overflow-hidden">
                        <img
                          src={game.urlImage || "/placeholder.svg"}
                          alt={game.name}
                          className="h-76 w-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />

                        {/* Top badges */}
                        <div className="absolute left-3 top-3">
                          <Badge
                            className={`text-[10px] px-1.5 py-0 border-0 ${
                              game.isActive
                                ? "bg-emerald-500/90 text-white"
                                : "bg-zinc-800/80 text-zinc-300"
                            }`}
                          >
                            {game.isActive ? "Activo" : "Inactivo"}
                          </Badge>
                        </div>

                        {/* Bottom overlay */}
                        <div className="absolute bottom-0 left-0 right-0 p-3 flex items-end justify-between">
                          <div>
                            <h3 className="font-bold text-base text-white leading-tight drop-shadow">
                              {game.name}
                            </h3>
                            <p className="text-xs text-white/75 flex items-center gap-1 mt-0.5">
                              <Trophy className="h-3 w-3" />
                              {Number(game.totalTournaments || 0)} torneos
                            </p>
                          </div>
                          {game.urlActiveSeason && (
                            <div className="shrink-0 rounded-full bg-white/20 backdrop-blur-sm p-1.5 transition-transform duration-200 group-hover:scale-110">
                              <ArrowRight className="h-3.5 w-3.5 text-white" />
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Body */}
                      <div className="p-3">
                        {game.description && (
                          <p className="text-xs text-muted-foreground line-clamp-2 mb-2">
                            {game.description}
                          </p>
                        )}
                        {game.urlActiveSeason ? (
                          <Button
                            variant="outline"
                            size="sm"
                            className="w-full text-xs transition-colors group-hover:bg-primary group-hover:text-primary-foreground group-hover:border-primary"
                            onClick={(e) => {
                              e.stopPropagation()
                              handleGameClick(game)
                            }}
                          >
                            Ver Temporada Activa
                            <ExternalLink className="ml-1.5 h-3.5 w-3.5" />
                          </Button>
                        ) : (
                          <p className="text-xs text-muted-foreground text-center py-1">
                            Sin temporada activa
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {games.length === 0 && !gamesError && !gamesLoading && (
                <div className="rounded-2xl border border-dashed border-border bg-card/50 py-12 text-center">
                  <Gamepad2 className="mx-auto mb-4 h-16 w-16 text-muted-foreground" />
                  <p className="text-lg text-muted-foreground">No hay juegos activos disponibles</p>
                </div>
              )}
            </section>
          </div>
        </section>
      </div>
    </Layout>
  )
}

export default Ranking
